import { useEffect, useRef, useState } from 'react';
import { browserCameraAdapter, isCameraError, type CameraAdapter } from '../data/camera';
import {
  POSE_SKELETON_EDGES,
  detectKeypointsFromVideo,
  type NormalizedKeypoint,
} from '../ml/moveNet';
import './WebcamPanel.css';

type Status = 'opening' | 'ready' | 'error';

/** How often the live skeleton overlay re-runs MoveNet while the
 *  capture panel is open. 10 Hz matches the predict panel and stays
 *  smooth on a $200 Chromebook without saturating the CPU. */
const POSE_OVERLAY_INTERVAL_MS = 100;
/** Below this confidence we skip drawing a keypoint / edge, so the
 *  overlay stays calm when the kid is half out of frame instead of
 *  flickering low-confidence dots all over the wall behind them. */
const POSE_OVERLAY_MIN_SCORE = 0.3;

/**
 * Default capture rate while the kid is recording. 24 fps matches
 * Teachable Machine's default (kids comparing the two should get
 * similarly dense bursts) and modern browsers' canvas+IndexedDB
 * pipelines handle that easily on a $200 Chromebook. The capture
 * loop already drops frames when a write is in flight, so slower
 * devices degrade gracefully rather than melting.
 */
export const DEFAULT_HOLD_FPS = 24;
/** Hard upper bound — kids tweaking the gear-icon settings can't push
 *  the capture loop past this. The cap is here, not just in the dialog
 *  input, so a malformed `captureSettings.fps` from a saved-then-opened
 *  `.bryta` file can't melt the device either. */
export const MAX_HOLD_FPS = 30;

export interface WebcamPanelProps {
  /** Called with each captured PNG blob. May be invoked many times
   *  during a single hold. For pose projects, `opts.poseKeypoints`
   *  carries the MoveNet keypoints detected on the same frame so the
   *  caller can persist them alongside the blob (so the thumbnail and
   *  modal can render the skeleton without re-running MoveNet). */
  onCapture: (
    blob: Blob,
    opts?: { poseKeypoints?: ReadonlyArray<import('../data/types').SamplePoseKeypoint> },
  ) => void | Promise<void>;
  onClose: () => void;
  /** Hold-to-record FPS — defaults to 4. Clamped at MAX_HOLD_FPS. */
  fps?: number;
  /** When false, click-to-record-once-after-delay timer mode replaces
   *  the hold-to-record gesture. Currently always falls back to true —
   *  TM-style timer mode is wired but not yet exposed in the UI. */
  holdToRecord?: boolean;
  /** Countdown (in seconds) before timer-mode recording starts. */
  delaySeconds?: number;
  /** Burst length (in seconds) when in timer mode. */
  durationSeconds?: number;
  /** For tests; defaults to the real browser MediaDevices adapter. */
  camera?: CameraAdapter;
  /** When true, run MoveNet on the live preview and draw keypoint
   *  dots + skeleton edges over the video. Pose projects only — for
   *  image projects the cost (and the visual) isn't justified. */
  showPoseSkeleton?: boolean;
}

export function WebcamPanel({
  onCapture,
  onClose,
  fps = DEFAULT_HOLD_FPS,
  holdToRecord = true,
  delaySeconds = 0,
  durationSeconds = 6,
  camera = browserCameraAdapter,
  showPoseSkeleton = false,
}: WebcamPanelProps): JSX.Element {
  const safeFps = Math.max(1, Math.min(MAX_HOLD_FPS, Math.round(fps)));
  const HOLD_INTERVAL_MS = 1000 / safeFps;
  const safeDelay = Math.max(0, Math.round(delaySeconds));
  const safeDuration = Math.max(1, Math.round(durationSeconds));
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  // Cache of the last keypoints detected by the overlay loop. Read at
  // capture time so we don't pay for a second MoveNet inference on
  // every shutter — the overlay runs at 10 Hz, faster than any of our
  // capture rates, so the cached keypoints are at most ~100 ms old
  // (visually identical to the live overlay the kid was looking at).
  const latestKeypointsRef = useRef<readonly NormalizedKeypoint[] | null>(null);
  const [status, setStatus] = useState<Status>('opening');
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  // Timer mode (holdToRecord === false): two short-lived phases run in
  // sequence on a single click — `delay` (count down to start) then
  // `recording` (count down to stop). When `phase === 'idle'` the
  // button reads "Record N seconds"; the countdown number is the
  // seconds left in whichever phase is active.
  const [phase, setPhase] = useState<'idle' | 'delay' | 'recording'>('idle');
  const [countdown, setCountdown] = useState(0);
  const [openAttempt, setOpenAttempt] = useState(0);

  // Skip a tick if the previous capture is still writing — better to drop
  // a frame than queue a backlog that the kid never asked for.
  const inFlight = useRef(false);
  const intervalHandle = useRef<ReturnType<typeof setInterval> | null>(null);
  // Timer-mode bookkeeping — separate from intervalHandle so a clean-up
  // tears down both the per-second tick and the per-frame burst loop.
  const timerHandle = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopAtRef = useRef<number>(0);
  // Suppress the synthesized click that follows a pointerup so we don't
  // double-capture when the activation came from a mouse.
  const pointerHandled = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let opened: MediaStream | null = null;

    setStatus('opening');
    setError(null);

    (async (): Promise<void> => {
      let stream: MediaStream;
      try {
        stream = await camera.open();
      } catch (err) {
        if (cancelled) return;
        const message = isCameraError(err) ? err.message : 'Could not turn on the camera.';
        setError(message);
        setStatus('error');
        return;
      }
      if (cancelled) {
        camera.close(stream);
        return;
      }
      opened = stream;
      if (videoRef.current) {
        try {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;
          await videoRef.current.play().catch(() => {});
        } catch {
          // Best-effort attach; non-fatal in jsdom or restricted contexts.
        }
      }
      if (!cancelled) setStatus('ready');
    })();

    return () => {
      cancelled = true;
      if (intervalHandle.current) {
        clearInterval(intervalHandle.current);
        intervalHandle.current = null;
      }
      if (timerHandle.current) {
        clearInterval(timerHandle.current);
        timerHandle.current = null;
      }
      if (opened) camera.close(opened);
    };
  }, [camera, openAttempt]);

  const captureOne = async (): Promise<void> => {
    if (!videoRef.current || status !== 'ready' || inFlight.current) return;
    inFlight.current = true;
    try {
      const video = videoRef.current;
      const blob = await camera.capture(video);
      // Pose projects: attach the most recent keypoints from the
      // overlay loop (cached in latestKeypointsRef). Re-detecting
      // here would double the per-frame cost; the cached value is
      // ≤ 100 ms old which matches what the kid was seeing in the
      // viewfinder a moment before they hit shutter. If the cache is
      // empty (overlay hasn't ticked yet, or no person was in frame),
      // we ship the blob without keypoints and the overlay simply
      // doesn't render on that thumbnail.
      const cached = showPoseSkeleton ? latestKeypointsRef.current : null;
      const opts = cached ? { poseKeypoints: cached } : undefined;
      await onCapture(blob, opts);
    } catch (err) {
      const message = isCameraError(err) ? err.message : 'Could not save the picture.';
      setError(message);
    } finally {
      inFlight.current = false;
    }
  };

  const startHold = (): void => {
    if (status !== 'ready' || recording) return;
    setRecording(true);
    setError(null);
    void captureOne();
    intervalHandle.current = setInterval(() => {
      void captureOne();
    }, HOLD_INTERVAL_MS);
  };

  const stopHold = (): void => {
    if (intervalHandle.current) {
      clearInterval(intervalHandle.current);
      intervalHandle.current = null;
    }
    setRecording(false);
  };

  // Timer-mode burst: optionally count down `safeDelay` seconds, then
  // capture for `safeDuration` seconds at safeFps, then return to idle.
  // Clicking again while a countdown / burst is running cancels both
  // and resets — so the kid can bail out without waiting it out.
  const startTimerRun = (): void => {
    if (status !== 'ready' || phase !== 'idle') return;
    setError(null);

    const beginRecording = (): void => {
      stopAtRef.current = Date.now() + safeDuration * 1000;
      setPhase('recording');
      setRecording(true);
      setCountdown(safeDuration);
      // First frame on the leading edge of the burst, then catch the
      // rest at the configured fps. captureOne is reentrancy-safe
      // (inFlight guard) so we don't queue a backlog on slow writes.
      void captureOne();
      intervalHandle.current = setInterval(() => {
        void captureOne();
      }, HOLD_INTERVAL_MS);
      // 1 Hz countdown ticker — drives the visible "Recording Xs…"
      // label. When the duration is up we tear everything down.
      timerHandle.current = setInterval(() => {
        const remaining = Math.max(0, Math.ceil((stopAtRef.current - Date.now()) / 1000));
        setCountdown(remaining);
        if (remaining === 0) finishTimerRun();
      }, 250);
    };

    if (safeDelay === 0) {
      beginRecording();
      return;
    }
    setPhase('delay');
    setCountdown(safeDelay);
    const startAt = Date.now() + safeDelay * 1000;
    timerHandle.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((startAt - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) {
        if (timerHandle.current) {
          clearInterval(timerHandle.current);
          timerHandle.current = null;
        }
        beginRecording();
      }
    }, 250);
  };

  const finishTimerRun = (): void => {
    if (intervalHandle.current) {
      clearInterval(intervalHandle.current);
      intervalHandle.current = null;
    }
    if (timerHandle.current) {
      clearInterval(timerHandle.current);
      timerHandle.current = null;
    }
    setRecording(false);
    setPhase('idle');
    setCountdown(0);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>): void => {
    if (status !== 'ready') return;
    if (!holdToRecord) return; // timer mode handles activation in onClick
    e.currentTarget.setPointerCapture(e.pointerId);
    pointerHandled.current = true;
    startHold();
  };

  const handlePointerEnd = (): void => {
    if (!holdToRecord) return;
    if (!recording) return;
    stopHold();
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (!holdToRecord) {
      // Timer mode: any click toggles. If we're idle, kick off the
      // delay → record sequence. If we're already mid-run, cancel it
      // so the kid can abort without waiting the full duration.
      if (phase === 'idle') startTimerRun();
      else finishTimerRun();
      return;
    }
    // Hold-to-record mode — mouse activations were already handled by
    // pointerdown; only run for keyboard activation (detail === 0).
    if (pointerHandled.current) {
      pointerHandled.current = false;
      return;
    }
    if (e.detail !== 0) return;
    void captureOne();
  };

  // Live pose-skeleton overlay loop. Runs only on pose projects (cost
  // of MoveNet ≈ 30–60 ms per frame on a Chromebook; we'd rather not
  // pay that for image projects). Independent of the capture timer
  // so it keeps drawing while the kid is just lining themselves up
  // before recording.
  useEffect(() => {
    if (!showPoseSkeleton || status !== 'ready') return;
    let stopped = false;
    let inFlight = false;
    // First failure stops the loop entirely. Without this guard a
    // dead model URL or missing dependency would have us hammering
    // the failing fetch every 100 ms — 600 doomed requests / min.
    // We log the first error so the cause is visible in devtools.
    let handle: number | null = null;
    const teardown = (): void => {
      if (handle !== null) {
        window.clearInterval(handle);
        handle = null;
      }
    };
    const tick = async (): Promise<void> => {
      if (stopped || inFlight) return;
      const video = videoRef.current;
      const canvas = overlayRef.current;
      if (!video || !canvas) return;
      inFlight = true;
      try {
        const keypoints = await detectKeypointsFromVideo(video);
        if (stopped) return;
        latestKeypointsRef.current = keypoints;
        drawPoseSkeleton(canvas, video, keypoints);
      } catch (err) {
        console.warn('[bryta] pose-skeleton overlay disabled:', err);
        teardown();
      } finally {
        inFlight = false;
      }
    };
    void tick();
    handle = window.setInterval(() => {
      void tick();
    }, POSE_OVERLAY_INTERVAL_MS);
    const overlayAtMount = overlayRef.current;
    return () => {
      stopped = true;
      teardown();
      const ctx = overlayAtMount?.getContext('2d');
      if (ctx && overlayAtMount) {
        ctx.clearRect(0, 0, overlayAtMount.width, overlayAtMount.height);
      }
    };
  }, [showPoseSkeleton, status]);

  const handleRetry = (): void => {
    setOpenAttempt((n) => n + 1);
  };

  // Two completely different button affordances:
  //   - Hold mode: "Hold to record" idle, "Recording…" while held.
  //   - Timer mode: "Record N seconds" idle, "Starting in Xs…" during
  //     the optional delay, "Recording Xs…" while the burst plays out.
  // Phrasing the timer label as a verb + duration is the cue the kid
  // gave us — they expect the button to *announce* what it's about to
  // do, not just describe a gesture.
  let captureLabel: string;
  let captureAria: string;
  if (holdToRecord) {
    captureLabel = recording ? 'Recording…' : 'Hold to record';
    captureAria = recording ? 'Recording. Release to stop' : 'Hold to record';
  } else if (phase === 'delay') {
    captureLabel = `Starting in ${countdown}s…`;
    captureAria = `Recording starts in ${countdown} seconds. Click to cancel.`;
  } else if (phase === 'recording') {
    captureLabel = `Recording ${countdown}s…`;
    captureAria = `Recording, ${countdown} seconds left. Click to stop.`;
  } else {
    captureLabel = `Record ${safeDuration} seconds`;
    captureAria = `Record ${safeDuration} seconds`;
  }
  const captureHint = holdToRecord
    ? 'Press and hold to record a burst. Tap once to take a single picture.'
    : `Click to record a ${safeDuration}-second burst${
        safeDelay > 0 ? ` after a ${safeDelay}-second countdown` : ''
      }. Click again to stop early.`;

  return (
    <div className="bl-webcam" role="group" aria-label="Webcam capture">
      <div className="bl-webcam__viewfinder">
        <video
          ref={videoRef}
          className="bl-webcam__video"
          playsInline
          muted
          aria-label="Live camera preview"
        />
        {showPoseSkeleton && (
          <canvas ref={overlayRef} className="bl-webcam__pose-overlay" aria-hidden="true" />
        )}
        {/* X close button anchored in the top-right of the viewfinder
            — TM-style. Compact icon hits the same target without
            stealing a whole row from the controls strip. */}
        <button
          type="button"
          className="bl-webcam__close"
          onClick={onClose}
          aria-label="Close camera"
        >
          ✕
        </button>
        {recording && (
          <span className="bl-webcam__rec" aria-hidden="true">
            ● REC
          </span>
        )}
        {status === 'opening' && (
          <p className="bl-webcam__overlay" role="status">
            Turning on the camera…
          </p>
        )}
        {status === 'error' && (
          <div className="bl-webcam__overlay bl-webcam__overlay--error" role="alert">
            <p>{error}</p>
            <button type="button" className="bl-webcam__retry" onClick={handleRetry}>
              Try again
            </button>
          </div>
        )}
      </div>
      <p className="bl-webcam__hint">{captureHint}</p>
      <div className="bl-webcam__controls">
        <button
          type="button"
          className={`bl-webcam__capture${recording ? ' bl-webcam__capture--recording' : ''}`}
          disabled={status !== 'ready'}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onClick={handleClick}
          aria-label={captureAria}
        >
          {captureLabel}
        </button>
      </div>
      {status === 'ready' && error && (
        <p role="alert" className="bl-webcam__capture-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Paint the keypoints + skeleton edges onto the overlay canvas, sized
 * to match the visible video. Lines first, then dots on top, so joints
 * read as crisp circles even where edges cross. `null` keypoints mean
 * "no person in frame" — clear the canvas so the kid sees an honest
 * "you're out of frame" instead of a stale pose stuck on the screen.
 */
function drawPoseSkeleton(
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
  keypoints: readonly NormalizedKeypoint[] | null,
): void {
  const w = video.clientWidth;
  const h = video.clientHeight;
  if (w === 0 || h === 0) return;
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  if (!keypoints) return;

  // Match the colour to the theme so the overlay reads on Lab notebook
  // (light bg) AND Riso / Default (dark bg). Cyan with high saturation
  // sits well against most camera scenes; the white halo gives it a
  // legible edge in over-exposed frames.
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(56, 189, 248, 0.95)';
  ctx.shadowColor = 'rgba(15, 23, 42, 0.45)';
  ctx.shadowBlur = 4;
  for (const [a, b] of POSE_SKELETON_EDGES) {
    const ka = keypoints[a];
    const kb = keypoints[b];
    if (!ka || !kb) continue;
    if (ka.score < POSE_OVERLAY_MIN_SCORE || kb.score < POSE_OVERLAY_MIN_SCORE) continue;
    ctx.beginPath();
    ctx.moveTo(ka.x * w, ka.y * h);
    ctx.lineTo(kb.x * w, kb.y * h);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(56, 189, 248, 0.95)';
  for (const kp of keypoints) {
    if (kp.score < POSE_OVERLAY_MIN_SCORE) continue;
    ctx.beginPath();
    ctx.arc(kp.x * w, kp.y * h, 5, 0, Math.PI * 2);
    ctx.fill();
  }
}
